/**
 * Cart Error Handling Utilities
 * 
 * Provides standardized error messages and error handling for cart operations.
 */

export enum CartErrorType {
  INSUFFICIENT_STOCK = "INSUFFICIENT_STOCK",
  INVALID_PRODUCT = "INVALID_PRODUCT",
  INVALID_VARIANT = "INVALID_VARIANT",
  NETWORK_ERROR = "NETWORK_ERROR",
  UNAUTHORIZED = "UNAUTHORIZED",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

export interface CartError {
  type: CartErrorType;
  message: string;
  description?: string;
  availableStock?: number;
}

/**
 * Parse error from Convex mutation and return structured CartError
 */
export function parseCartError(error: unknown): CartError {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    // Insufficient stock error
    if (message.includes("stock") || message.includes("available")) {
      const stockMatch = message.match(/only (\d+) items? available/i);
      const availableStock = stockMatch ? parseInt(stockMatch[1], 10) : undefined;

      return {
        type: CartErrorType.INSUFFICIENT_STOCK,
        message: "Insufficient stock",
        description: error.message,
        availableStock,
      };
    }

    // Invalid product error
    if (message.includes("product not found") || message.includes("product does not exist")) {
      return {
        type: CartErrorType.INVALID_PRODUCT,
        message: "Product not found",
        description: "This product is no longer available",
      };
    }

    // Invalid variant error
    if (message.includes("variant") || message.includes("invalid selection")) {
      return {
        type: CartErrorType.INVALID_VARIANT,
        message: "Invalid variant selected",
        description: "Please select a valid product variant",
      };
    }

    // Unauthorized error
    if (message.includes("unauthorized") || message.includes("permission")) {
      return {
        type: CartErrorType.UNAUTHORIZED,
        message: "Unauthorized",
        description: "You don't have permission to perform this action",
      };
    }

    // Network/connection error
    if (message.includes("network") || message.includes("connection") || message.includes("timeout")) {
      return {
        type: CartErrorType.NETWORK_ERROR,
        message: "Network error",
        description: "Please check your internet connection and try again",
      };
    }

    // Unknown error with original message
    return {
      type: CartErrorType.UNKNOWN_ERROR,
      message: "Failed to update cart",
      description: error.message,
    };
  }

  // Non-Error object
  return {
    type: CartErrorType.UNKNOWN_ERROR,
    message: "An unexpected error occurred",
    description: "Please try again",
  };
}

/**
 * Format error message for display to user
 */
export function formatCartErrorMessage(error: CartError): string {
  switch (error.type) {
    case CartErrorType.INSUFFICIENT_STOCK:
      if (error.availableStock !== undefined) {
        if (error.availableStock === 0) {
          return "This item is currently out of stock";
        }
        return `Only ${error.availableStock} item${error.availableStock === 1 ? "" : "s"} available`;
      }
      return error.description || error.message;

    case CartErrorType.INVALID_PRODUCT:
      return "This product is no longer available";

    case CartErrorType.INVALID_VARIANT:
      return "Please select a valid product variant";

    case CartErrorType.NETWORK_ERROR:
      return "Network error. Please check your connection and try again";

    case CartErrorType.UNAUTHORIZED:
      return "You don't have permission to perform this action";

    case CartErrorType.UNKNOWN_ERROR:
    default:
      return error.description || error.message || "An unexpected error occurred";
  }
}

/**
 * Log cart error for monitoring
 */
export function logCartError(
  operation: string,
  error: CartError,
  context?: {
    userId?: string;
    productId?: string;
    variantId?: string;
    quantity?: number;
  }
): void {
  console.error(`[Cart Error] ${operation}:`, {
    type: error.type,
    message: error.message,
    description: error.description,
    availableStock: error.availableStock,
    context,
    timestamp: new Date().toISOString(),
  });

  // Report to PostHog Error Tracking
  if (typeof window !== "undefined") {
    import("posthog-js").then(({ default: posthog }) => {
      posthog.captureException(new Error(error.message), {
        source: "cart_error",
        operation,
        errorType: error.type,
        ...context,
      });
    });
  }
}

/**
 * Handle cart operation error with toast notification and logging
 */
export function handleCartError(
  operation: string,
  error: unknown,
  context?: {
    userId?: string;
    productId?: string;
    variantId?: string;
    quantity?: number;
  }
): CartError {
  const cartError = parseCartError(error);
  logCartError(operation, cartError, context);
  return cartError;
}
